import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { ThemeProvider, useTheme } from '../../../../../theme';
import { useIsMobile } from '../../../../../utils';

import { useExchangeBreadcrumbs } from '../../lib/useExchangeBreadcrumbs';
import { useExchangeRouteControls } from '@features/exchange/exchange-route-controls';
import { useHtmlDescriptionOfError } from '../../../../../Modules/Exchange/components/Exchange/hooks';
import Dialog from '../../../../../Modules/Exchange/components/Exchange/Dialog/Dialog';
import WarningModal from '../../../../../WarningModal';
import PromoLoader from '../../../../../PromoLoader';
import SimpleLoader from '../../../../../SimpleLoader';

import { ExchangeRefundBreadcrumbs, ExchangeRefundHeader, ExchangeRefundMobileBreadcrumbs } from '@entities/order';
import {
	PaymentStatus,
	clearError,
	clearPaymentStatus,
	getErrorState,
	getPaymentStatusState,
	getIsLoadingExchange
} from '@entities/exchange';

const Layout: React.FC = ({ children }) => {
	const { t } = useTranslation('Exchange');
	const { Layout: theme } = useTheme('Widgets').Exchange;

	const dispatch = useDispatch();

	const error = useSelector(getErrorState);
	const paymentStatus = useSelector(getPaymentStatusState);
	const isLoading = useSelector(getIsLoadingExchange);

	const isMobile = useIsMobile();

	const exchangeRouteControls = useExchangeRouteControls();
	const exchangeBreadcrumbs = useExchangeBreadcrumbs();

	const errorHtml = useHtmlDescriptionOfError(error ? [error] : null);

	const onClearError = () => {
		dispatch(clearError());
	};

	const onClearPaymentStatus = () => {
		dispatch(clearPaymentStatus());
	};

	const showDesktopBreadcrumbs = !isMobile && !!exchangeBreadcrumbs.length;
	const showMobileBreadcrumbs = isMobile && !!exchangeBreadcrumbs.length;

	return (
		<ThemeProvider
			value={{
				BaseComponents: {
					Breadcrumbs: { breadcrumbs: theme.breadcrumbs },
					BreadcrumbItem: { breadcrumb: theme.breadcrumb, icon_end: theme.breadcrumb__iconEnd }
				}
			}}
		>
			<div className={theme.root}>
				<ExchangeRefundHeader
					title={t('Layout.Title')}
					breadcrumbs={
						showDesktopBreadcrumbs && <ExchangeRefundBreadcrumbs breadcrumbs={exchangeBreadcrumbs} />
					}
					isLoading={isLoading || exchangeRouteControls.isUpdatingOrder}
					onClose={exchangeRouteControls.onBackToOrder}
					onGoBackward={exchangeRouteControls.onBack}
				/>
				{exchangeRouteControls.isUpdatingOrder && <SimpleLoader />}
				{isLoading && <PromoLoader />}
				<div className={cn(theme.content, { [theme.content_loading]: isLoading })}>
					{paymentStatus && (
						<Dialog
							text={t(paymentStatus === PaymentStatus.Success ? 'Successful payment' : 'Failed payment')}
							acceptText={t('OK')}
							onAccept={onClearPaymentStatus}
							open={!!paymentStatus}
						/>
					)}
					{error && (
						<WarningModal
							isOpen={!!error}
							onClose={onClearError}
							title={t('Oops...')}
							content={
								errorHtml?.header ? (
									<div dangerouslySetInnerHTML={{ __html: errorHtml.header }} />
								) : (
									error
								)
							}
							errorMessage={
								errorHtml?.body && <div dangerouslySetInnerHTML={{ __html: errorHtml.body }} />
							}
						/>
					)}
					{showMobileBreadcrumbs && <ExchangeRefundMobileBreadcrumbs breadcrumbs={exchangeBreadcrumbs} />}
					{children}
				</div>
			</div>
		</ThemeProvider>
	);
};

export default Layout;
